import { useQuery } from '@tanstack/react-query';
import { CguDto } from '../../../models/user-membership/dto/user-membership.dto';
import { useApiClient } from '../../../apiClient';

/**
 * GET /organizations/:orgId/membership-types/:typeId/cgu
 *
 * Returns the resolved CGU (org default OR per-type override, whichever applies)
 * or null when neither is configured (server returns 204).
 *
 * Used by the operator dashboard to:
 *   - Show the "Currently in effect" preview when editing the org CGU
 *   - Pre-fill the per-type override editor when an operator first toggles
 *     "Use custom CGU"
 *
 * Same endpoint mobile uses pre-purchase. The dashboard caller is admin-authed
 * via Firebase JWT; the API enforces same-org tenancy on the GET.
 */
export const GET_CGU_QUERY_KEY = (
  organizationId: string,
  membershipTypeId: string,
) => ['cgu', organizationId, membershipTypeId];

export const useGetCgu = (
  organizationId: string | undefined,
  membershipTypeId: string | undefined,
  options?: { enabled?: boolean },
) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: GET_CGU_QUERY_KEY(organizationId ?? '', membershipTypeId ?? ''),
    enabled:
      Boolean(organizationId) &&
      Boolean(membershipTypeId) &&
      options?.enabled !== false,
    // DASHBOARD-41: this used to wrap the call in a `catch` that mapped
    // `err?.status === 204` to `null`, on the stated premise that "the fetchApi
    // wrapper may throw on empty bodies depending on its implementation". It
    // does not, and that branch was unreachable for the whole life of this
    // hook: `isResponseValid` admits every 2xx, so a 204 takes the SUCCESS
    // path and `fetchApi` returns the parsed body. The real failure was one
    // layer down — the parse yielded `undefined`, the only value React Query v4
    // refuses from a `queryFn`, so this read reported itself as an ERROR for
    // the ordinary case of a membership type with no CGU, and `/admin/memberships`
    // reached Sentry in prod. Fixed in `getJsonFromResponse` (empty body →
    // `null`) rather than here, because every read of an empty-bodied 2xx has
    // the same defect and a per-hook `?? null` is a fork of a shared decision.
    //
    // So the `null` in this hook's contract above is now delivered by the
    // client, and a 204 is an ANSWER rather than a failure. A genuine transport
    // or authorization failure still rejects and still sets `isError`.
    queryFn: () =>
      apiClient.fetchApi<CguDto, {}>({
        method: 'GET',
        endpoint: `organizations/${organizationId}/membership-types/${membershipTypeId}/cgu`,
      }),
  });
};
